/* eslint-disable jsx-a11y/label-has-associated-control */
import { useMutation } from '@tanstack/react-query';
import { Select, SelectMultiple, TextInput, ThemeContext } from 'grommet';
import type { JSX } from 'preact';
import { TargetedEvent, useEffect, useId, useState } from 'preact/compat';
import { useDispatch } from 'react-redux';
import { trackMP } from 'Shared/mp';

import { patchUserProfileMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { updateUserProperty } from '@/redux/slices/user/actions';
import type { GradeNumber, ISOAlpha2Code, SubjectEnum } from '@/redux/slices/user/types';

import { COUNTRIES, COUNTRY_TO_ISO, ISO_TO_COUNTRY } from './constants/countries';
import {
  ENUM_TO_SUBJECT,
  GRADE_OPTIONS,
  GRADE_TO_NUMBER,
  NUMBER_TO_GRADE,
  SUBJECT_OPTIONS,
  SUBJECT_TO_ENUM,
} from './constants/educatorOptions';
import { flagAppOnboardingModalStep, unflagAppOnboardingModalStep } from './redux/actions';

export interface EducatorDetailsForm {
  location: string;
  grades: string[];
  subjects: string[];
  schoolName: string;
}

interface CustomSearchInputProps {
  style?: JSX.CSSProperties;
  value?: string;
  onChange?: (event: any) => void;
  placeholder?: string;
}

const CustomSearchInput = ({ style = {}, value, onChange, placeholder }: CustomSearchInputProps) => (
  <TextInput style={{ fontSize: '16px', ...style }} value={value} onChange={onChange} placeholder={placeholder} />
);

const CONTROL_BASE_STYLE = `
  width: 100%;
  border-radius: 6px;
  border: 1px solid #CBD5E1;
  background: #FFF;
  font-size: 16px;
  box-sizing: border-box;
`;

const SELECT_CONTROL_STYLE = `
  ${CONTROL_BASE_STYLE}
  height: 40px;
  padding: 0 2px 0 4px;
`;

const SELECT_MULTIPLE_CONTROL_STYLE = `
  ${CONTROL_BASE_STYLE}
  min-height: 40px;
  padding: 6px 2px 0 4px;
`;

const formatSelectedOptions = (selectedOptions: string[], maxVisible: number): string => {
  if (selectedOptions.length === 0) return '';
  if (selectedOptions.length <= maxVisible) {
    return selectedOptions.join(', ');
  }
  const visible = selectedOptions.slice(0, maxVisible).join(', ');
  const remaining = selectedOptions.length - maxVisible;
  return `${visible} (+ ${remaining} more)`;
};

const localTheme = {
  select: {
    searchInput: CustomSearchInput,
    container: { extend: 'width: 100%;' },
    control: { extend: SELECT_CONTROL_STYLE },
  },
  selectMultiple: {
    container: { extend: 'width: 100%;' },
    control: { extend: SELECT_MULTIPLE_CONTROL_STYLE },
  },
};

const EducatorDetails = () => {
  const dispatch = useDispatch();
  const formId = useId();
  const countryName = useSelector((state) => state.user.referralData?.country_name);
  const existingEducatorDetails = useSelector((state) => state.user.educatorDetails);

  const [formData, setFormData] = useState<EducatorDetailsForm>(() => {
    if (existingEducatorDetails) {
      return {
        location: ISO_TO_COUNTRY[existingEducatorDetails.location] || ISO_TO_COUNTRY[countryName ?? ''] || '',
        grades: existingEducatorDetails.grades.map((grade) => NUMBER_TO_GRADE[grade]).filter(Boolean),
        subjects: existingEducatorDetails.subjects.map((subject) => ENUM_TO_SUBJECT[subject]).filter(Boolean),
        schoolName: existingEducatorDetails.schoolName,
      };
    }
    return {
      location: ISO_TO_COUNTRY[countryName ?? ''] || '',
      grades: [],
      subjects: [],
      schoolName: '',
    };
  });

  const [countryOptions, setCountryOptions] = useState(COUNTRIES);

  const { mutate: saveEducatorDetails, isPending } = useMutation({
    mutationFn: patchUserProfileMutationFn,
    onSuccess: (_data, variables) => {
      const { location, grades, subjects, schoolName } = variables.educatorDetails ?? {};
      if (location && grades && subjects && schoolName) {
        dispatch(updateUserProperty({ educatorDetails: { location, grades, subjects, schoolName } }));
      }
    },
    onError: (error) => {
      console.error('Failed to save educator details:', error);
    },
  });

  const handleInputChange = (field: keyof EducatorDetailsForm, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountrySearch = (text: string) => {
    const filteredCountries =
      text === '' ? COUNTRIES : COUNTRIES.filter((country) => country.toLowerCase().includes(text.toLowerCase()));
    setCountryOptions(filteredCountries);
  };

  const handleCountryChange = ({ value }: { value: string }) => {
    handleInputChange('location', value);
    setCountryOptions(COUNTRIES);
  };

  const handleSubmit = (e: TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();

    const backendData = {
      location: COUNTRY_TO_ISO[formData.location] as ISOAlpha2Code,
      grades: formData.grades.map((grade) => GRADE_TO_NUMBER[grade]) as GradeNumber[],
      subjects: formData.subjects.map((subject) => SUBJECT_TO_ENUM[subject]) as SubjectEnum[],
      schoolName: formData.schoolName,
    };

    saveEducatorDetails({ educatorDetails: backendData });
  };

  useEffect(() => {
    const isCompleted = !!(
      existingEducatorDetails?.location &&
      existingEducatorDetails?.grades?.length &&
      existingEducatorDetails?.subjects?.length &&
      existingEducatorDetails?.schoolName
    );
    if (isCompleted) {
      dispatch(flagAppOnboardingModalStep('educatorDetails'));
      trackMP('after-signup-step', {
        step: 'educatorDetails',
        location: existingEducatorDetails.location,
        grades: existingEducatorDetails.grades,
        subjects: existingEducatorDetails.subjects,
        schoolName: existingEducatorDetails.schoolName,
      });
    }
  }, [dispatch, existingEducatorDetails]);

  const isFormValid = !!(formData.location && formData.grades.length && formData.subjects.length && formData.schoolName);

  return (
    <ThemeContext.Extend value={localTheme}>
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="shrink-0">
          <h1 className="preflight preflight-h1 px-5 pt-10 text-center text-2xl/7 font-bold text-[#162B1B]">
            Tell us about yourself
          </h1>
          <p className="preflight preflight-p text-normal/6 mb-5 mt-2 text-center text-base text-gray-500">
            Help us personalize your
            <br />
            teaching experience
          </p>
        </div>

        <div className="flex min-h-0 grow flex-col gap-6 overflow-y-auto px-10 pb-5">
          <div className="flex flex-col gap-2">
            <label
              id={`${formId}-location-label`}
              htmlFor={`${formId}-location`}
              className="text-base font-semibold text-[#162B1B]"
            >
              Where are you based?
            </label>
            <Select
              id={`${formId}-location`}
              aria-labelledby={`${formId}-location-label`}
              options={countryOptions}
              value={formData.location}
              onChange={handleCountryChange}
              onSearch={handleCountrySearch}
              placeholder="Select Country"
              searchPlaceholder="Search countries..."
              emptySearchMessage="No countries found"
              dropHeight="medium"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              id={`${formId}-grades-label`}
              htmlFor={`${formId}-grades`}
              className="text-base font-semibold text-[#162B1B]"
            >
              Which grades do you teach?
            </label>
            <SelectMultiple
              id={`${formId}-grades`}
              aria-labelledby={`${formId}-grades-label`}
              options={GRADE_OPTIONS}
              value={formData.grades}
              onChange={({ value }: { value: string[] }) => handleInputChange('grades', value)}
              placeholder="Select grades"
              showSelectedInline={false}
              messages={{ multiple: formatSelectedOptions(formData.grades, 2) }}
              dropHeight="small"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              id={`${formId}-subjects-label`}
              htmlFor={`${formId}-subjects`}
              className="text-base font-semibold text-[#162B1B]"
            >
              Which subjects do you teach?
            </label>
            <SelectMultiple
              id={`${formId}-subjects`}
              aria-labelledby={`${formId}-subjects-label`}
              options={SUBJECT_OPTIONS}
              value={formData.subjects}
              onChange={({ value }: { value: string[] }) => handleInputChange('subjects', value)}
              placeholder="Select subjects"
              showSelectedInline={false}
              messages={{ multiple: formatSelectedOptions(formData.subjects, 1) }}
              dropHeight="small"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              id={`${formId}-school-label`}
              htmlFor={`${formId}-school`}
              className="text-base font-semibold text-[#162B1B]"
            >
              What is the name of your school?
            </label>
            <input
              id={`${formId}-school`}
              aria-labelledby={`${formId}-school-label`}
              type="text"
              placeholder="Type Here"
              value={formData.schoolName}
              onChange={(e) => handleInputChange('schoolName', e.currentTarget.value)}
              className="h-10 w-full rounded-md border-[1px] border-solid border-[#CBD5E1] bg-white px-3 text-base focus:border-primary-green focus:outline-none focus:ring-1 focus:ring-primary-green"
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3 px-10 py-5">
          <button
            type="button"
            disabled={isPending}
            onClick={() => {
              dispatch(updateUserProperty({ persona: null }));
              dispatch(unflagAppOnboardingModalStep('tailorExperience'));
            }}
            className="preflight preflight-button w-[96px] shrink-0 rounded-full border border-gray-400 p-2 text-sm/6 font-medium text-gray-500 hover:bg-secondary-green hover:text-white"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isPending || !isFormValid}
            className="preflight preflight-button w-full rounded-full bg-primary-green p-2 text-sm/6 font-medium text-white hover:bg-secondary-green"
          >
            {isPending ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </form>
    </ThemeContext.Extend>
  );
};

export default EducatorDetails;
